import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Navigate } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useSlug } from "@/hooks/useSlug";
import { useGeneralQueries } from "@/queries/general/queryOptions";
import { useOnboardingScreenQueries } from "@/queries/onboarding-screens/queryOptions";
import { selfAccountMutations } from "@/queries/self-account/mutations";
import { routes } from "@/routes";

import { OnboardingSlides } from "./OnboardingSlides";

export function OnboardingSlidesPage(): React.ReactNode {
  const [isUserLanguageDetermined, userLanguageDeterminedHandlers] = useBool(false);

  const slug = useSlug();
  const sessionUser = useSessionUser();
  const onboardingScreenQueries = useOnboardingScreenQueries();
  const generalQueries = useGeneralQueries();
  const { data: uiLanguages = [] } = useQuery(generalQueries.languages({ subset: "ui" }));

  const changeLanguageMutation = selfAccountMutations.useUpdateLanguage();

  const { data: onboardingScreens, error: onboardingScreensError } = useQuery({
    ...onboardingScreenQueries.list(),
    enabled: isUserLanguageDetermined,
  });

  useEffect(() => {
    if (!uiLanguages.length) return;

    if (isUserLanguageDetermined) return;

    const systemLanguage = navigator.language.split("-")[0];
    const currUserLanguage = sessionUser.language.id;
    const isSystemLanguageSupported = uiLanguages.some((language) => language.id === systemLanguage);

    if (isSystemLanguageSupported ? systemLanguage !== currUserLanguage : currUserLanguage !== "en") {
      // Fallback to English if the system language is not one of the supported languages
      void changeLanguageMutation
        .mutateAsync({ uiLanguageId: isSystemLanguageSupported ? systemLanguage : "en" })
        .finally(() => {
          userLanguageDeterminedHandlers.setTrue();
        });
    } else {
      userLanguageDeterminedHandlers.setTrue();
    }
  }, [
    changeLanguageMutation,
    isUserLanguageDetermined,
    sessionUser.language.id,
    uiLanguages,
    userLanguageDeterminedHandlers,
  ]);

  if (!onboardingScreens) {
    return null;
  }

  if (onboardingScreens.length == 0) {
    return <Navigate to={routes.onboardingFlow.userProfile({ slug })} replace />;
  }

  if (sessionUser.onboardedAt) {
    return <Navigate to="/" />;
  }

  if (onboardingScreensError) {
    return <ErrorPage error={onboardingScreens} />;
  }

  if (!isUserLanguageDetermined) {
    return <FullSizeLoader withPadding />;
  }

  return <OnboardingSlides slides={onboardingScreens} />;
}
